import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Lock, RefreshCw, Copy, Loader2, AlertTriangle, Code2,
} from "lucide-react";
import { toast } from "sonner";
import { partnerApi, formatPartnerError, canPartnerAdmin } from "./partnerApi";
import "./PartnerPortal.css";

export default function PartnerOAuth() {
  const { me } = useOutletContext();
  const canWrite = canPartnerAdmin(me);
  const [clients, setClients] = useState([]);
  const [meta, setMeta] = useState({});
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freshSecret, setFreshSecret] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        partnerApi.get("/partner/portal/oauth/clients"),
        partnerApi.get("/partner/portal/oauth/audit?limit=50"),
      ]);
      setClients(c.data.clients || []);
      setMeta({ token_endpoint: c.data.token_endpoint,
                 token_lifetime_seconds: c.data.token_lifetime_seconds });
      setAudit(a.data.events || []);
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rotate = async (c) => {
    if (!window.confirm("Rotate this OAuth client's secret? Existing tokens continue to work until expiry (30 min).")) return;
    try {
      const { data } = await partnerApi.post(`/partner/portal/oauth/clients/${c.client_id}/rotate-secret`);
      setFreshSecret({ client_id: data.id, secret: data.token, rotated_from: data.rotated_from });
      toast.success("OAuth client rotated — save the new client_secret NOW.");
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div data-testid="pp-oauth">
      <h1 className="hw-pp__page-title">
        <Lock className="w-5 h-5"
                style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        OAuth 2.0 Clients
      </h1>
      <p className="hw-pp__page-sub">
        Server-to-server integrations use the <code style={{ color: "#5EEAD4" }}>client_credentials</code> grant.
        Mint keys with the <code>oauth:client</code> scope on the API Keys page,
        then exchange them at the token endpoint.
      </p>

      {freshSecret && (
        <div className="hw-pp__panel" data-testid="pp-oauth-fresh"
              style={{ borderColor: "#F59E0B" }}>
          <h3>
            <span>
              <AlertTriangle className="w-4 h-4"
                              style={{ display: "inline", verticalAlign: "text-bottom",
                                        marginRight: "0.3rem", color: "#F59E0B" }} />
              New client_secret — copy now
            </span>
            <button className="hw-pp__btn" onClick={() => setFreshSecret(null)}>Dismiss</button>
          </h3>
          <div className="hw-pp__reveal-hint">
            The old client_id has been revoked. Update your integration to use the new credentials below.
          </div>
          <div style={{ fontSize: "0.75rem", color: "#94A3B8", marginBottom: "0.3rem" }}>
            client_id (new): <code data-testid="pp-oauth-fresh-id">{freshSecret.client_id}</code>
          </div>
          <div className="hw-pp__reveal" data-testid="pp-oauth-fresh-secret">{freshSecret.secret}</div>
          <button className="hw-pp__btn" onClick={() => copy(freshSecret.secret)}>
            <Copy className="w-3.5 h-3.5" /> Copy client_secret
          </button>
        </div>
      )}

      <div className="hw-pp__panel">
        <h3>
          <span><Code2 className="w-4 h-4"
                        style={{ display: "inline", verticalAlign: "text-bottom",
                                  marginRight: "0.3rem" }} />
            Token endpoint</span>
          <small>Token lifetime: {meta.token_lifetime_seconds || 1800}s</small>
        </h3>
        <div className="hw-pp__reveal" style={{ background: "rgba(15,23,42,0.7)",
                                                    borderStyle: "solid", borderColor: "rgba(94,234,212,0.15)" }}>
          POST {meta.token_endpoint || "/api/oauth/token"}
          {"\n"}Authorization: Basic base64(client_id:client_secret)
          {"\n"}Content-Type: application/x-www-form-urlencoded
          {"\n"}grant_type=client_credentials&scope=&lt;space-separated-subset&gt;
        </div>
      </div>

      <div className="hw-pp__panel">
        <h3>
          <span>{clients.length} OAuth client{clients.length === 1 ? "" : "s"}</span>
          <button className="hw-pp__btn" onClick={load}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </h3>
        {loading && !clients.length ? (
          <div style={{ color: "#5EEAD4" }}>
            <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
          </div>
        ) : clients.length === 0 ? (
          <div style={{ color: "#64748B", fontSize: "0.85rem", padding: "0.5rem 0" }}>
            No OAuth clients yet. Head to <a href="/partner/portal/api-keys" style={{ color: "#5EEAD4" }}>API Keys</a> and
            mint a key with the <code>oauth:client</code> scope.
          </div>
        ) : (
          <table className="hw-pp__table" data-testid="pp-oauth-clients-table">
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Name</th>
                <th>Authorized scopes</th>
                <th>Tokens issued</th>
                <th>Last used</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.client_id} data-testid={`pp-oauth-row-${c.client_id}`}>
                  <td className="hw-pp__mono">{c.client_id.slice(0, 8)}…</td>
                  <td><strong style={{ color: "#F1F5F9" }}>{c.name}</strong></td>
                  <td>
                    {(c.scopes || []).map((s) => (
                      <span key={s} className="hw-pp__scope-chip">{s}</span>
                    ))}
                  </td>
                  <td>{c.request_count}</td>
                  <td style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                    {c.last_used_at ? c.last_used_at.slice(0, 10) : "—"}
                  </td>
                  <td>
                    <span className={`hw-pp__pill ${c.revoked ? "hw-pp__pill--revoked" : "hw-pp__pill--active"}`}>
                      {c.revoked ? "Revoked" : "Active"}
                    </span>
                  </td>
                  <td>
                    {!c.revoked && canWrite && (
                      <button className="hw-pp__btn"
                               style={{ padding: "0.2rem 0.4rem" }}
                               onClick={() => rotate(c)}
                               data-testid={`pp-oauth-rotate-${c.client_id}`}>
                        <RefreshCw className="w-3 h-3" /> Rotate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="hw-pp__panel">
        <h3>OAuth audit history</h3>
        {audit.length === 0 ? (
          <div style={{ color: "#64748B", fontSize: "0.85rem" }}>
            No OAuth events yet. Rotations, token exchanges, and revocations appear here.
          </div>
        ) : (
          <table className="hw-pp__table" data-testid="pp-oauth-audit-table">
            <thead>
              <tr><th>When</th><th>Action</th><th>Actor</th></tr>
            </thead>
            <tbody>
              {audit.slice(0, 50).map((e, idx) => (
                <tr key={idx}>
                  <td style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                    {(e.at || "").slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="hw-pp__mono">{e.action}</td>
                  <td>{e.actor_email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
